import { styled } from '@linaria/react';
import type { FC } from 'react';
import { useEffect } from 'react';
import { fullscreenQuery } from '../../core/mapV2/queries';
import { PressEscapeCommand } from '../../core/mapV2/commands/PressEscapeCommand';
import { fullscreenSet } from '../../core/mapV2/state';
import { useAppDispatch, useAppSelector } from '../../types';
import { EditNotice } from './layout/EditNotice';
import { KpiBar } from './layout/KpiBar';
import { LeftPanel } from './layout/LeftPanel';
import { RightPanel } from './layout/RightPanel';
import { StatusBar } from './layout/StatusBar';
import { MapCanvas } from './map/MapCanvas';
import { EditReferenceFieldDialog } from './panels/edit/EditReferenceFieldDialog';

const Page = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    overflow: hidden;
`;

const Row = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: row;
`;

const Column = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
`;

// Shell of the map v2 page (see docs/map-v2/plan.md, Phase 1):
// KPI bar on top, layers panel on the left, map in the middle, object sheet on the right, status bar below.
// In fullscreen only the map remains; Esc brings the panels back.
export const MapV2Page: FC = () => {
    const dispatch = useAppDispatch();
    const fullscreen = useAppSelector(fullscreenQuery);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            // Open menus and dialogs (MUI) handle their own Esc; do not also close the panels behind them.
            if (event.key === 'Escape' && !event.defaultPrevented) {
                dispatch(PressEscapeCommand());
            }
        };
        window.addEventListener('keydown', onKeyDown);

        return () => window.removeEventListener('keydown', onKeyDown);
    }, [dispatch]);

    // Leaving the page must not leave the next visit in fullscreen.
    useEffect(
        () => () => {
            dispatch(fullscreenSet(false));
        },
        [dispatch],
    );

    return (
        <Page data-testid="map-v2-page">
            {fullscreen ? null : <KpiBar />}
            <Row>
                {fullscreen ? null : <LeftPanel />}
                <Column>
                    <MapCanvas />
                    {fullscreen ? null : <StatusBar />}
                </Column>
                {fullscreen ? null : <RightPanel />}
            </Row>
            <EditReferenceFieldDialog />
            <EditNotice />
        </Page>
    );
};
